import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    Fab,
    Button,
    IconButton,
    Divider,
    Checkbox,
    FormControlLabel,
    CircularProgress,
    Grid,
    useMediaQuery
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ListIcon from '@mui/icons-material/List';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/Share';
import SendIcon from '@mui/icons-material/Send';
import dayjs from 'dayjs';
import { setShowGraph } from 'store/slice/EcomSlice';
import MembershipTree from '../components/MembershipTree';
import AddUserActionModal from '../action/AddUser/AddUserActionModal';
import { setMlmuserAddUserModal, setSelectedMlmuserId } from 'store/slice/DmpSlice';
import { sendReminderToInviteeAPI } from 'api/CoreMlmmgmtWebServices';

export default function MlmuserMyTeamPage() {
    const dispatch = useDispatch();
    const { mlmuserList } = useSelector((state) => state.dmpslice);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'graph'
    const [showPendingOnly, setShowPendingOnly] = useState(false);
    const [resentUserId, setResentUserId] = useState(null);
    const [resendLoadingId, setResendLoadingId] = useState(null);

    const handleAddAssociate = () => {
        dispatch(setSelectedMlmuserId(-1));
        dispatch(setMlmuserAddUserModal(true));
    };

    const toggleViewMode = () => {
        const newMode = viewMode === 'list' ? 'graph' : 'list';
        setViewMode(newMode);
        dispatch(setShowGraph(newMode === 'graph'));
    };

    const handleEdit = (user) => {
        dispatch(setSelectedMlmuserId(user.id));
        dispatch(setMlmuserAddUserModal(true));
    };

    const handleShare = async (user) => {
        dispatch(setSelectedMlmuserId(user.id));
        let index = mlmuserList.map(mlmuserObj => mlmuserObj.id).indexOf(user.id);
        let url = window.location.href;
        let urlPrefix = url.substr(0, url.lastIndexOf('/'))
        const linkToCopy = urlPrefix + "/?inviteelogin=" + mlmuserList[index].mobilenumber;
        console.log('linkToCopy===>' + linkToCopy);
        try {
          await navigator.clipboard.writeText(linkToCopy);
          alert("Link copied to clipboard!");
          console.log("Link copied=>" + linkToCopy);
        } catch (err) {
          console.error("Failed to copy text: ", err);
          alert("Failed to copy the link. Please try again.");
        }
    };

    const handlePendingFilterChange = (event) => {
        setShowPendingOnly(event.target.checked);
    };

    const getFilteredList = () => {
        if (!mlmuserList) return [];
        
        let filtered = [...mlmuserList];
        
        if (showPendingOnly) {
            filtered = filtered.filter(user => user.paymentstatus?.toLowerCase() !== 'captured');
        }
        
        return filtered.reverse();
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    const resendWabaMsgToInvitee = (user) => {
        setResendLoadingId(user.id);
        // Replace with your actual API call
        sendReminderToInviteeAPI(
            { inviteeId: user.id },
            () => {
                setResentUserId(user.id);
                setResendLoadingId(null);
                setTimeout(() => setResentUserId(null), 3000);
            },
            () => {
                setResendLoadingId(null);
                alert("Failed to resend invite. Please try again.");
            }
        );
    };

    const UserCard = ({ user }) => {
        return (
            <Card sx={{ mb: 2, boxShadow: 3, borderRadius: 2 }}>
                <CardContent>
                    {/* Header with User Info and Status Icons */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                {user.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                {user.mobilenumber}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            {/* Payment Status */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                {user.paymentstatus?.toLowerCase() === 'captured' ? (
                                    <>
                                        <CheckCircleIcon 
                                            sx={{ 
                                                color: 'success.main', 
                                                fontSize: 32,
                                                mb: 0.5
                                            }} 
                                        />
                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'success.main' }}>
                                            Payment
                                        </Typography>
                                    </>
                                ) : (
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Chip
                                            label="Payment Pending"
                                            size="small"
                                            color="error"
                                            sx={{ 
                                                fontWeight: 600,
                                                fontSize: '0.7rem'
                                            }}
                                        />
                                        <IconButton 
                                            size="small" 
                                            onClick={() => handleEdit(user)}
                                            sx={{ 
                                                color: 'primary.main',
                                                '&:hover': { bgcolor: 'primary.50' }
                                            }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton 
                                            size="small" 
                                            onClick={() => handleShare(user)}
                                            sx={{ 
                                                color: 'primary.main',
                                                '&:hover': { bgcolor: 'primary.50' }
                                            }}
                                        >
                                            <ShareIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                            
                            {/* Partnership Status - Only show if payment status exists */}
                            {user.paymentstatus && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    {user.partnershipstatus?.toUpperCase() === 'CONFIRMED' ? (
                                        <>
                                            <CheckCircleIcon 
                                                sx={{ 
                                                    color: 'success.main', 
                                                    fontSize: 32,
                                                    mb: 0.5
                                                }} 
                                            />
                                            <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'success.main' }}>
                                                Association
                                            </Typography>
                                        </>
                                    ) 
                                    : 
                                    (
                                        <></>
                                    )
                                    }
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {user.partnershipstatus?.toUpperCase() !== 'CONFIRMED' && (
                        <>
                            <Divider sx={{ my: 1.5 }} />
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%',
                                py: 0.5
                            }}>
                                {user.invitesenton && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                        Invited on {formatDate(user.invitesenton)}
                                    </Typography>
                                )}
                                <Box sx={{ flex: 1 }} />
                                {user.paymentstatus?.toLowerCase() !== 'captured' && (
                                    (() => {
                                        // Disable resend if less than 2 days since last invite
                                        let resendDisabled = false;
                                        if (user.invitesenton) {
                                            const inviteDate = dayjs(user.invitesenton);
                                            const now = dayjs();
                                            resendDisabled = now.diff(inviteDate, 'day') < 2;
                                        }
                                        return (
                                            resentUserId === user.id ? (
                                                <Typography variant="caption" color="success.main" sx={{ fontWeight: 600, fontSize: '0.7rem', ml: 2 }}>
                                                    Message sent
                                                </Typography>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    size="small"
                                                    startIcon={
                                                        resendLoadingId === user.id
                                                            ? <CircularProgress size={16} />
                                                            : <SendIcon fontSize="small" />
                                                    }
                                                    sx={{
                                                        ml: 2,
                                                        minWidth: 120,
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem',
                                                        opacity: resendLoadingId === user.id || resendDisabled ? 0.6 : 1,
                                                        pointerEvents: resendLoadingId === user.id || resendDisabled ? 'none' : 'auto'
                                                    }}
                                                    disabled={resendLoadingId === user.id || resendDisabled}
                                                    onClick={() => {
                                                        if (!(resendLoadingId === user.id || resendDisabled)) {
                                                            resendWabaMsgToInvitee(user);
                                                        }
                                                    }}
                                                >
                                                    {resendLoadingId === user.id ? 'Sending...' : 'Resend Invite'}
                                                </Button>
                                            )
                                        );
                                    })()
                                )}
                            </Box>
                        </>
                    )}
                </CardContent>
            </Card>
        );
    };

    const filteredList = getFilteredList();

    // Responsive breakpoints
    const isTablet = useMediaQuery('(min-width:600px) and (max-width:1199px)');
    const isLaptop = useMediaQuery('(min-width:1200px)');
    const cardsPerRow = isLaptop ? 4 : isTablet ? 3 : 1;

    return (
        <Box sx={{ pb: 4, position: 'relative' }}>
            {/* View Toggle Button and Filter */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                gap: 2
            }}>
                {viewMode === 'list' && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showPendingOnly}
                                onChange={handlePendingFilterChange}
                                size="small"
                            />
                        }
                        sx={{ ml: 1 }}
                        label={<Typography variant="body2">Payment Pending</Typography>}
                    />
                )}

                <Button
                    variant="contained"
                    onClick={toggleViewMode}
                    startIcon={viewMode === 'list' ? <AccountTreeIcon /> : <ListIcon />}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        ml: viewMode === 'graph' ? 'auto' : 0
                    }}
                >
                    {viewMode === 'list' ? 'Graph' : 'List'}
                </Button>
            </Box>

            {/* Content based on view mode */}
            {viewMode === 'list' ? (
                <Box
                    sx={{
                        maxHeight: isLaptop ? '75vh' : isTablet ? '65vh' : '52vh',
                        padding: 1,
                        overflow: 'auto',
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                    }}
                >
                    {/* User List as Grid */}
                    <Grid container spacing={2}>
                        {filteredList && filteredList.length > 0 ? (
                            filteredList.map((user, idx) => (
                                <Grid item xs={12} sm={12} md={12 / cardsPerRow} lg={12 / cardsPerRow} key={user.id}>
                                    <UserCard user={user} />
                                </Grid>
                            ))
                        ) : (
                            <Grid item xs={12}>
                                <Card sx={{ textAlign: 'center', py: 6 }}>
                                    <CardContent>
                                        <PersonIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                            No Associates Found under YOU
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {showPendingOnly ? 'No Associates with payment pending under You' : 'Your associates will appear here'}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            ) : (
                <MembershipTree />
            )}

            {/* Floating Add Associate Button */}
            <Fab
                variant="extended"
                color="primary"
                aria-label="add associate"
                onClick={handleAddAssociate}
                sx={{
                    position: 'fixed',
                    bottom: 80,
                    right: 16,
                    zIndex: 1000
                }}
            >
                <AddIcon sx={{ mr: 1 }} />
                Add Associate
            </Fab>
            <AddUserActionModal />
        </Box>
    );
}